"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.assignYAxes = exports.getYAxes = exports.getXAxes = void 0;
var tslib_1 = require("tslib");
// (C) 2007-2021 GoodData Corporation
var sdk_ui_1 = require("@gooddata/sdk-ui");
var common_1 = require("../_util/common");
var chartCapabilities_1 = require("./chartCapabilities");
var isEmpty_1 = tslib_1.__importDefault(require("lodash/isEmpty"));
var compact_1 = tslib_1.__importDefault(require("lodash/compact"));
var findIndex_1 = tslib_1.__importDefault(require("lodash/findIndex"));
var range_1 = tslib_1.__importDefault(require("lodash/range"));
var includes_1 = tslib_1.__importDefault(require("lodash/includes"));
function preprocessMeasureGroupItems(measureGroup, defaultValues) {
    return measureGroup.items.map(function (item, index) {
        var unwrapped = common_1.unwrap(item);
        return index
            ? {
                label: unwrapped.name,
                format: unwrapped.format,
            }
            : {
                label: defaultValues.label || unwrapped.name,
                format: defaultValues.format || unwrapped.format,
            };
    });
}
function getXAxes(dv, config, measureGroup, viewByAttribute) {
    var type = config.type;
    if (common_1.isScatterPlot(type) || common_1.isBubbleChart(type)) {
        var measureGroupItems = preprocessMeasureGroupItems(measureGroup, {
            label: config.xLabel,
            format: config.xFormat,
        });
        var firstMeasureGroupItem = measureGroupItems[0];
        var noPrimaryMeasures = dv.def().isBucketEmpty(sdk_ui_1.BucketNames.MEASURES);
        if (noPrimaryMeasures) {
            return [
                {
                    label: "",
                },
            ];
        }
        else {
            return [
                {
                    label: firstMeasureGroupItem.label || "",
                    format: firstMeasureGroupItem.format || "",
                },
            ];
        }
    }
    var xLabel = config.xLabel || (viewByAttribute ? viewByAttribute.formOf.name : "");
    return [
        {
            label: xLabel,
        },
    ];
}
exports.getXAxes = getXAxes;
function getYAxes(dv, config, measureGroup, stackByAttribute) {
    var type = config.type;
    var measureGroupItems = preprocessMeasureGroupItems(measureGroup, {
        label: config.yLabel,
        format: config.yFormat,
    });
    var firstMeasureGroupItem = measureGroupItems[0];
    var secondMeasureGroupItem = measureGroupItems[1];
    var hasMoreThanOneMeasure = measureGroupItems.length > 1;
    var noPrimaryMeasures = dv.def().isBucketEmpty(sdk_ui_1.BucketNames.MEASURES);
    var _a = ((common_1.isBarChart(type) ? config.secondary_xaxis : config.secondary_yaxis) || {}).measures, secondaryAxisMeasures = _a === void 0 ? [] : _a;
    var yAxes = [];
    if (common_1.isScatterPlot(type) || common_1.isBubbleChart(type)) {
        var hasSecondaryMeasure = !dv.def().isBucketEmpty(sdk_ui_1.BucketNames.SECONDARY_MEASURES);
        if (hasSecondaryMeasure) {
            if (noPrimaryMeasures) {
                yAxes = [
                    tslib_1.__assign({}, firstMeasureGroupItem),
                ];
            }
            else {
                yAxes = [
                    tslib_1.__assign({}, secondMeasureGroupItem),
                ];
            }
        }
        else {
            yAxes = [{ label: "" }];
        }
    }
    else if (common_1.isHeatmap(type)) {
        yAxes = [
            {
                label: stackByAttribute ? stackByAttribute.formOf.name : "",
            },
        ];
    }
    else if (common_1.isOneOfTypes(type, chartCapabilities_1.supportedDualAxesChartTypes) &&
        !isEmpty_1.default(measureGroupItems) &&
        !isEmpty_1.default(secondaryAxisMeasures)) {
        var _b = assignMeasuresToAxes(secondaryAxisMeasures, measureGroup), measuresInFirstAxis = _b.measuresInFirstAxis, measuresInSecondAxis = _b.measuresInSecondAxis;
        var firstAxis = createYAxisItem(measuresInFirstAxis, false);
        var secondAxis = createYAxisItem(measuresInSecondAxis, true);
        if (firstAxis) {
            firstAxis = tslib_1.__assign(tslib_1.__assign({}, firstAxis), { seriesIndices: measuresInFirstAxis.map(function (_a) {
                    var index = _a.index;
                    return index;
                }) });
        }
        if (secondAxis) {
            secondAxis = tslib_1.__assign(tslib_1.__assign({}, secondAxis), { seriesIndices: measuresInSecondAxis.map(function (_a) {
                    var index = _a.index;
                    return index;
                }) });
        }
        yAxes = compact_1.default([firstAxis, secondAxis]);
    }
    else {
        // if more than one measure and NOT dual, then have empty item name
        var nonDualMeasureAxis = hasMoreThanOneMeasure
            ? {
                label: "",
            }
            : {};
        yAxes = [
            tslib_1.__assign(tslib_1.__assign(tslib_1.__assign({}, firstMeasureGroupItem), nonDualMeasureAxis), { seriesIndices: range_1.default(measureGroupItems.length) }),
        ];
    }
    return yAxes;
}
exports.getYAxes = getYAxes;
function assignMeasuresToAxes(secondMeasures, measureGroup) {
    return measureGroup.items.reduce(function (result, _a, index) {
        var _b = _a.measureHeaderItem, name = _b.name, format = _b.format, localIdentifier = _b.localIdentifier;
        if (includes_1.default(secondMeasures, localIdentifier)) {
            result.measuresInSecondAxis.push({ name: name, format: format, index: index });
        }
        else {
            result.measuresInFirstAxis.push({ name: name, format: format, index: index });
        }
        return result;
    }, {
        measuresInFirstAxis: [],
        measuresInSecondAxis: [],
    });
}
function createYAxisItem(measuresInAxis, opposite) {
    if (opposite === void 0) { opposite = false; }
    var length = measuresInAxis.length;
    if (length) {
        var _a = measuresInAxis[0], name_1 = _a.name, format = _a.format;
        return {
            label: length === 1 ? name_1 : "",
            format: format,
            opposite: opposite,
        };
    }
    return null;
}
function assignYAxes(series, yAxes) {
    return series.reduce(function (result, item, index) {
        var yAxisIndex = findIndex_1.default(yAxes, function (axis) {
            var _a;
            return includes_1.default((_a = axis.seriesIndices) !== null && _a !== void 0 ? _a : [], index);
        });
        // for case viewBy and stackBy have one attribute, and one measure is sliced to multiple series
        // then 'yAxis' in other series should follow the first one
        var firstYAxisIndex = result.length > 0 ? result[0].yAxis : 0;
        var seriesItem = tslib_1.__assign(tslib_1.__assign({}, item), { yAxis: yAxisIndex !== -1 ? yAxisIndex : firstYAxisIndex });
        result.push(seriesItem);
        return result;
    }, []);
}
exports.assignYAxes = assignYAxes;
//# sourceMappingURL=chartAxes.js.map